import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, ShieldAlert, Target } from 'lucide-react';
import type { DashboardMetrics, ArgentinaMetrics, CountryCode } from '../utils/calculations';
import { formatCurrency } from '../utils/calculations';
import { Tooltip } from './Tooltip';
interface CriticalThresholdsProps {
  metrics: DashboardMetrics;
  arMetrics?: ArgentinaMetrics;
  country: CountryCode;
}
export function CriticalThresholds({
  metrics,
  arMetrics,
  country
}: CriticalThresholdsProps) {
  const isAr = country === 'AR' && arMetrics;
  const cpaBreakEven = isAr ? arMetrics.cpaBreakEven : metrics.cpaBreakEven;
  const roasBreakEven = isAr ? arMetrics.roasBreakEven : metrics.roasBreakEven;
  const cpaTarget10 = isAr ? arMetrics.cpaTarget10 : metrics.cpaTarget10;
  const cpaTarget20 = isAr ? arMetrics.cpaTarget20 : metrics.cpaTarget20;
  const cpaRatio = !isAr && cpaBreakEven > 0 ? Math.min(metrics.cpaIdeal / cpaBreakEven * 100, 100) : 0;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.5,
    delay: 0.3
  }} className="glass-card p-6 h-full flex flex-col border-danger-red/20" data-id="element-33">
      <div className="flex items-center gap-3 mb-6" data-id="element-34">
        <div className="p-2 bg-danger-red/10 rounded-lg" data-id="element-35">
          <AlertTriangle className="w-5 h-5 text-danger-red" data-id="element-36" />
        </div>
        <h2 className="text-sm font-bold tracking-widest text-danger-red uppercase" data-id="element-37">
          Umbrales Críticos
        </h2>
      </div>

      <div className="space-y-5 flex-grow flex flex-col justify-center" data-id="element-38">
        {/* CPA Break-even */}
        <div className="relative" data-id="element-39">
          <div className="flex justify-between items-end mb-2" data-id="element-40">
            <div data-id="element-41">
              <div className="flex items-center gap-2 mb-1" data-id="element-42">
                <h3 className="text-xs font-bold text-white/60 uppercase" data-id="element-43">
                  CPA Break-Even
                </h3>
                <Tooltip text={isAr ? 'Margen Bruto disponible para Meta antes de perder dinero.' : 'Límite máximo en Meta Ads antes de perder dinero, considerando cancelaciones y devoluciones.'} data-id="element-44" />
              </div>
              <div className="text-2xl font-black text-danger-red drop-shadow-[0_0_8px_rgba(255,68,68,0.5)]" data-id="element-45">
                {formatCurrency(cpaBreakEven, country)}
              </div>
            </div>
            <TrendingDown className="w-5 h-5 text-danger-red/70 mb-1" data-id="element-46" />
          </div>

          {!isAr && <>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-47">
                <div className="h-full bg-gradient-to-r from-cyber-green via-yellow-400 to-danger-red relative transition-all duration-500" style={{
              width: `${cpaRatio}%`
            }} data-id="element-48">
                  <div className="absolute right-0 top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_white]" data-id="element-49"></div>
                </div>
              </div>
              <div className="flex justify-between mt-1.5" data-id="element-50">
                <span className="text-[10px] text-cyber-green/60 font-medium" data-id="element-51">
                  CPA Actual: {formatCurrency(metrics.cpaIdeal, country)}
                </span>
                <span className="text-[10px] text-danger-red/60 font-medium uppercase tracking-wider" data-id="element-52">
                  Límite de pérdida
                </span>
              </div>
            </>}
        </div>

        <div className="h-px w-full bg-white/5" data-id="element-53"></div>

        {/* ROAS de Supervivencia */}
        <div data-id="element-54">
          <div className="flex items-center gap-2 mb-2" data-id="element-55">
            <ShieldAlert className="w-4 h-4 text-danger-red/50" data-id="element-56" />
            <h3 className="text-xs font-bold text-danger-red/70 uppercase" data-id="element-57">
              ROAS de Supervivencia
            </h3>
          </div>
          <div className="flex items-baseline gap-2" data-id="element-58">
            <span className="text-3xl font-bold text-danger-red" data-id="element-59">
              {roasBreakEven.toFixed(2)}
            </span>
            <span className="text-xs text-white/40 font-medium" data-id="element-60">
              Mínimo para no perder
            </span>
          </div>
        </div>

        <div className="h-px w-full bg-white/5" data-id="element-61"></div>

        {/* Fases de Utilidad */}
        <div data-id="element-62">
          <div className="flex items-center gap-2 mb-3" data-id="element-63">
            <Target className="w-4 h-4 text-cyber-green/60" data-id="element-64" />
            <h3 className="text-xs font-bold text-white/60 uppercase" data-id="element-65">
              Fases de Utilidad
            </h3>
          </div>
          <div className="space-y-2" data-id="element-66">
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-yellow-400/5 border border-yellow-400/10" data-id="element-67">
              <div data-id="element-68">
                <span className="text-[10px] font-bold text-yellow-400 uppercase tracking-wider" data-id="element-69">
                  Validación (10%)
                </span>
                <p className="text-[9px] text-white/30" data-id="element-70">CPA Objetivo en Meta</p>
              </div>
              <span className="text-sm font-black text-yellow-400" data-id="element-71">
                {formatCurrency(cpaTarget10, country)}
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-cyber-green/5 border border-cyber-green/10" data-id="element-72">
              <div data-id="element-73">
                <span className="text-[10px] font-bold text-cyber-green uppercase tracking-wider" data-id="element-74">
                  Objetivo (20%)
                </span>
                <p className="text-[9px] text-white/30" data-id="element-75">CPA Objetivo en Meta</p>
              </div>
              <span className="text-sm font-black text-cyber-green" data-id="element-76">
                {formatCurrency(cpaTarget20, country)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}